import { v4 as uuidv4 } from 'uuid';
import { FileData, FileType, BoundingBox, Polygon, MarkedRange } from '../types/training';
import { FileInfo } from '../services/trainingSocketService';
import { getFileType } from '../utils/training';

// Helper function to determine file type from extension
// Convert FileData to FileInfo for socket communication
export const fileDataToFileInfo = (fileData: FileData): FileInfo => {
  return {
    name: fileData.name,
    type: fileData.type,
    size: fileData.size || 0, // Provide default size of 0 if undefined
    content: fileData.textContent || null,
    // Include annotations and marks if they exist
    annotations: fileData.annotations ? JSON.stringify(fileData.annotations) : null,
    marks: fileData.marks ? JSON.stringify(fileData.marks) : null
  };
};

// Helper to load a file from testfiles directory
export const loadFileFromDisk = async (directory: string, filename: string): Promise<FileData | null> => {
  try {
    const filePath = `/testfiles/${directory}/${filename}`;
    const response = await fetch(filePath);
    
    if (!response.ok) {
      console.error(`Failed to load file: ${filename}`);
      return null;
    }
    
    const type = getFileType(filename);
    const size = parseInt(response.headers.get('Content-Length') || '0', 10);
    let textContent: string | undefined;
    
    if (type === 'image') {
      const blob = await response.blob();
      // Convert blob to data URL for reliable storage
      textContent = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } else {
      textContent = await response.text();
    }
    
    // Create a file data object without annotations/marks by default
    const fileData: FileData = {
      id: uuidv4(),
      name: filename,
      type,
      size,
      textContent
    };

    // Only add annotations/marks to specific files to match what the user has manually marked
    
    // Demo image annotations - only for specific image files (first image and examples)
    if (type === 'image' && (filename === '2_1-Sat-Mar-08-17-40-53-2025-7.jpg' || 
                            filename === '5_1-Sat-Mar-08-17-40-56-2025-178.jpg' ||
                            filename.startsWith('3_1'))) {
      console.log(`Adding annotations to image file: ${filename}`);
      fileData.annotations = generateSampleAnnotations(fileData.id, true);
    }

    // Demo text marks - only for CSV files
    if (type === 'text' && filename === 'simulation_results.csv') {
      console.log(`Adding marks to text file: ${filename}`);
      fileData.marks = generateSampleMarks(fileData.id);
    }
    
    return fileData;
  } catch (error) {
    console.error(`Error loading file ${filename}:`, error);
    return null;
  }
};

// Load all files from a directory
const loadFilesFromDirectory = async (directory: string): Promise<FileData[]> => {
  try {
    // Fetch the directory listing (this would need to be implemented server-side)
    const response = await fetch(`/api/files/${directory}`);
    
    if (!response.ok) {
      // If API not available, fetch from a manifest file or return empty array
      console.warn(`Directory listing API not available for ${directory}, using static file list`);
      // Here you would implement fallback logic or return empty array
      return [];
    }
    
    const filenames = await response.json();
    const filePromises = filenames.map((filename: string) => 
      loadFileFromDisk(directory, filename)
    );
    
    const files = await Promise.all(filePromises);
    return files.filter((file): file is FileData => file !== null);
  } catch (error) {
    console.error(`Error loading directory ${directory}:`, error);
    return [];
  }
};

// This function would be called by the "Load Test Files" button
export const loadTestFiles = async (): Promise<{
  inputFiles: FileData[],
  outputFiles: FileData[],
  inputExampleFiles: FileData[],
  outputExampleFiles: FileData[]
}> => {
  // For inputFiles, load all files from the directory
  const allInputFiles = [
    // Original hardcoded files
    '2_1-Sat-Mar-08-17-40-53-2025-7.jpg',
    '2_2-Sat-Mar-08-17-40-53-2025-59.jpg',
    '2_3-Sat-Mar-08-17-40-54-2025-60.jpg',
    '3_1-Sat-Mar-08-17-40-54-2025-65.jpg',
    '3_2-Sat-Mar-08-17-40-55-2025-128.jpg',
    '3_3-Sat-Mar-08-17-40-55-2025-129.jpg',
    '4_1-Sat-Mar-08-17-40-55-2025-137.jpg',
    '4_2-Sat-Mar-08-17-40-55-2025-174.jpg',
    '4_3-Sat-Mar-08-17-40-55-2025-175.jpg',
    '5_1-Sat-Mar-08-17-40-56-2025-178.jpg',
    '5_2-Sat-Mar-08-17-40-56-2025-206.jpg',
    '5_3-Sat-Mar-08-17-40-56-2025-207.jpg',
    // Additional files (truncated for brevity - keeping only first batch)
    '6_1-Sat-Mar-08-17-40-56-2025-209.jpg',
    '6_2-Sat-Mar-08-17-40-59-2025-361.jpg',
    '6_3-Sat-Mar-08-17-40-59-2025-364.jpg'
  ];
  
  const inputExampleFileNames = [
    '2_1-Sat-Mar-08-17-40-53-2025-7.jpg',
    '2_2-Sat-Mar-08-17-40-53-2025-59.jpg',
    '2_3-Sat-Mar-08-17-40-54-2025-60.jpg'
  ];
  
  // Load files from each directory
  const inputFiles: FileData[] = [];
  for (const filename of allInputFiles) {
    const file = await loadFileFromDisk('inputfiles', filename);
    if (file) inputFiles.push(file);
  }
  
  const outputFiles: FileData[] = [];
  const outputFile = await loadFileFromDisk('outputfiles', 'simulation_results.csv');
  if (outputFile) outputFiles.push(outputFile);
  
  const inputExampleFiles: FileData[] = [];
  for (const filename of inputExampleFileNames) {
    const file = await loadFileFromDisk('inputexample', filename);
    if (file) inputExampleFiles.push(file);
  }
  
  const outputExampleFiles: FileData[] = [];
  const outputExampleFile = await loadFileFromDisk('outputexample', 'simulation_results.csv');
  if (outputExampleFile) outputExampleFiles.push(outputExampleFile);
  
  return {
    inputFiles,
    outputFiles,
    inputExampleFiles,
    outputExampleFiles
  };
};

// Load MNIST dataset from mnist directory
export const loadMnistFiles = async (): Promise<{
  inputFiles: FileData[],
  outputFiles: FileData[],
  inputExampleFiles: FileData[],
  outputExampleFiles: FileData[]
}> => {
  console.log('Loading MNIST dataset...');
  
  const inputFiles: FileData[] = [];
  const inputExampleFiles: FileData[] = [];
  const outputExampleFiles: FileData[] = [];
  
  // Load images from all digit folders (0-9)
  for (let digit = 0; digit <= 9; digit++) {
    try {
      // Try to load up to 100 images per digit
      for (let i = 1; i <= 100; i++) {
        const filename = `${i}.png`; // Assuming files are named 1.png, 2.png, etc.
        const filePath = `/mnist/${digit}/${filename}`;
        
        try {
          const response = await fetch(filePath);
          if (!response.ok) {
            // File doesn't exist, continue to next
            continue;
          }
          
          const blob = await response.blob();

          // Convert blob to data URL for reliable storage
          const imageUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });

          // Create file data with folder name in the filename to track which digit it is
          const fileData: FileData = {
            id: uuidv4(),
            name: `${digit}_${filename}`, // e.g., "0_1.png", "0_2.png"
            type: 'image',
            size: blob.size,
            textContent: imageUrl
          };
          
          inputFiles.push(fileData);
          
          // Add first image of folder 0 to input examples
          if (digit === 0 && i === 1) {
            inputExampleFiles.push({ ...fileData });
          }
        } catch (err) {
          // File doesn't exist or error loading, skip
          continue;
        }
      }
      
      console.log(`Loaded images from digit folder: ${digit}`);
    } catch (error) {
      console.error(`Error loading digit folder ${digit}:`, error);
    }
  }
  
  // Create a text file with "0" as the output example
  const outputExampleFile: FileData = {
    id: uuidv4(),
    name: 'label.txt',
    type: 'text',
    size: 1,
    textContent: '0'
  };
  outputExampleFiles.push(outputExampleFile);
  
  console.log(`MNIST loading complete: ${inputFiles.length} input files, ${inputExampleFiles.length} examples`);
  
  return {
    inputFiles,
    outputFiles: [], // No output files for MNIST
    inputExampleFiles,
    outputExampleFiles
  };
};

// Generate sample annotations for demonstration purposes
const generateSampleAnnotations = (fileId: string, isImage: boolean) => {
  if (!isImage) return undefined;
  
  // Extract any numeric part from the file ID to make unique positions
  const fileIdNumber = parseInt(fileId.replace(/\D/g, '').substring(0, 3)) || 50;
  const xOffset = (fileIdNumber % 100) + 50; // Between 50-150
  const yOffset = ((fileIdNumber * 1.5) % 100) + 50; // Different from xOffset
  
  // Create a bounding box annotation
  const bbox: BoundingBox = {
    id: uuidv4(),
    x: xOffset,
    y: yOffset,
    width: 100,
    height: 100,
    color: '#f87171', // red
    createdAt: Date.now()
  };
  
  // Create a polygon annotation with varied positions
  const polygon: Polygon = {
    id: uuidv4(),
    points: [
      { x: xOffset + 150, y: yOffset - 50 },
      { x: xOffset + 200, y: yOffset },
      { x: xOffset + 150, y: yOffset + 50 },
      { x: xOffset + 100, y: yOffset }
    ],
    color: '#60a5fa', // blue
    createdAt: Date.now()
  };
  
  // Add a second bounding box
  const bbox2: BoundingBox = {
    id: uuidv4(),
    x: xOffset + 250,
    y: yOffset + 150,
    width: 80,
    height: 120,
    color: '#34d399', // green
    createdAt: Date.now()
  };
  
  return [bbox, polygon, bbox2];
};

// Generate sample marks for demonstration purposes
const generateSampleMarks = (fileId: string): MarkedRange[] => {
  // Extract any numeric part from the file ID to make unique positions
  const fileIdNumber = parseInt(fileId.replace(/\D/g, '').substring(0, 3)) || 10;
  const offset1 = fileIdNumber % 20 + 5; // Generate varied starting positions
  const offset2 = (fileIdNumber * 2) % 30 + 40;
  const offset3 = (fileIdNumber * 3) % 40 + 80;
  
  // Create sample text marks
  return [
    {
      id: uuidv4(),
      start: offset1,
      end: offset1 + 15,
      color: '#fbbf24', // amber
      createdAt: Date.now()
    },
    {
      id: uuidv4(),
      start: offset2,
      end: offset2 + 25,
      color: '#34d399', // emerald
      createdAt: Date.now()
    },
    {
      id: uuidv4(),
      start: offset3,
      end: offset3 + 30,
      color: '#ec4899', // pink
      createdAt: Date.now()
    },
    {
      id: uuidv4(),
      start: offset1 + 100,
      end: offset1 + 120,
      color: '#8b5cf6', // purple
      createdAt: Date.now()
    }
  ];
};

